import moment from 'moment';
import React, { useMemo } from 'react';
import { Discipline, TissotCountdown } from '../../core/typings';
import { Image, Link } from '../../typings';
import { HeroBanner, ImageObject, SvgImage } from '../common';
import './styles.scss';

interface RaceEvent {
  date: string;
  dateDay: string;
  dateMonth: string;
  disciplineName: string;
  link: Link;
  location: string;
  title: string;
}

export interface CompetitionHeaderProps {
  backLink?: Link | null;
  broadcasterLogoImage?: Image | null;
  competitionLogo?: Image | null;
  competitionTitle: string;
  discipline?: Discipline | null;
  heroImage: Image;
  latestEvent?: RaceEvent;
  nextEvent?: RaceEvent;
  tissotCountdown?: TissotCountdown;
}

const CompetitionHeader = ({
  backLink,
  broadcasterLogoImage,
  competitionLogo,
  competitionTitle,
  discipline,
  heroImage,
  latestEvent,
  nextEvent,
}: CompetitionHeaderProps) => {
  const root = 'competition-header';
  const classes = [root];
  if (discipline) {
    classes.push(`${root}--discipline-${discipline}`);
  }
  const nextEventMoment = useMemo(
    () => (nextEvent ? moment(nextEvent.date) : null),
    [nextEvent],
  );
  const latestEventMoment = useMemo(
    () => (latestEvent ? moment(latestEvent.date) : null),
    [latestEvent],
  );

  // TODO: l10n "latest" / "next up" /  "watch on"
  return (
    <div className={classes.join(' ')}>
      <HeroBanner
        backLink={backLink}
        colorTheme="dark-grey"
        image={heroImage}
        imageSize="full"
        isDarkened
      >
        {competitionLogo && (
          <div className={`${root}__competition-logo`}>
            <ImageObject image={competitionLogo} />
          </div>
        )}

        <div className={`${root}__title-wrapper`}>
          <h1 className={`${root}__title`}>{competitionTitle}</h1>
        </div>

        {latestEvent && (
          <div className={`${root}__event-listing`}>
            <h3>Latest</h3>
            <div className={`${root}__event-listing__details`}>
              <div className={`${root}__event-listing__details__inner`}>
                <time
                  className={`${root}__event-listing__details__inner__date`}
                  dateTime={latestEventMoment?.toISOString()}
                >
                  <span className={`${root}__event-listing__details__inner__date__day`}>
                    {latestEvent.dateDay}
                  </span>
                  {' '}
                  <span className={`${root}__event-listing__details__inner__date__month`}>
                    {latestEvent.dateMonth}
                  </span>
                </time>

                <div className={`${root}__event-listing__details__inner__main`}>
                  <p className={`${root}__event-listing__details__inner__main__title`}>
                    {latestEvent.title}
                  </p>

                  <p className={`${root}__event-listing__details__inner__main__meta`}>
                    <span className={`${root}__event-listing__details__inner__main__meta__discipline`}>
                      {latestEvent.disciplineName}
                    </span>
                    &nbsp;
                    <span className={`${root}__event-listing__details__inner__main__meta__location`}>
                      {latestEvent.location}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            {latestEvent.link && (
              <div className={`${root}__event-listing__link`}>
                <a href={latestEvent.link.url}>
                  {latestEvent.link.title}
                  <SvgImage ariaHidden reference="#icon-long-arrow-right" />
                </a>
              </div>
            )}
          </div>
        )}

        {nextEvent && (
          <div className={`${root}__event-listing`}>
            <h3>Next up</h3>
            <div className={`${root}__event-listing__details`}>
              <div className={`${root}__event-listing__details__inner`}>
                <time
                  className={`${root}__event-listing__details__inner__date`}
                  dateTime={nextEventMoment?.toISOString()}
                >
                  <span className={`${root}__event-listing__details__inner__date__day`}>
                    {nextEvent.dateDay}
                  </span>
                  {' '}
                  <span className={`${root}__event-listing__details__inner__date__month`}>
                    {nextEvent.dateMonth}
                  </span>
                </time>

                <div className={`${root}__event-listing__details__inner__main`}>
                  <p className={`${root}__event-listing__details__inner__main__title`}>
                    {nextEvent.title}
                  </p>

                  <p className={`${root}__event-listing__details__inner__main__meta`}>
                    <span className={`${root}__event-listing__details__inner__main__meta__discipline`}>
                      {nextEvent.disciplineName}
                    </span>
                    {' '}
                    <span className={`${root}__event-listing__details__inner__main__meta__location`}>
                      {nextEvent.location}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {nextEvent.link && (
              <div className={`${root}__event-listing__link`}>
                <a href={nextEvent.link.url}>
                  {nextEvent.link.title}
                  <SvgImage ariaHidden reference="#icon-long-arrow-right" />
                </a>
              </div>
            )}

          </div>
        )}

        {broadcasterLogoImage && (
          <div className={`${root}__broadcaster`}>
            <p>Watch on</p>
            <ImageObject image={broadcasterLogoImage} fitType="contain" />
          </div>
        )}
      </HeroBanner>
    </div>
  );
};

export default CompetitionHeader;
